import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const BrandContext = createContext(null);

// Defaults (used before /api/config resolves)
const DEFAULT_BRAND = {
  center_name: "B.health® Local Benefits",
  center_short: "B.health®",
  tagline_fr: "Avantages locaux",
  tagline_nl: "Lokale voordelen",
  logo_url: "",
  favicon_url: "",
  city: "Berchem-Sainte-Agathe",
  region: "Bruxelles",
  country: "Belgique",
  address: "",
  color_primary: "#0b1220",
  color_primary_dark: "#060a12",
  color_accent: "#BFFF00",
  color_accent_dark: "#65a30d",
  email: "sante@bhealth.be",
  phone: "",
  website: "",
  instagram: "",
  facebook: "",
  linkedin: "",
  plan_adult_price: 69,
  plan_junior_price: 59,
  plan_adult_label_fr: "Plan Adult",
  plan_adult_label_nl: "Volwassene",
  plan_junior_label_fr: "Junior/Senior",
  plan_junior_label_nl: "Junior/Senior",
  hero_title_fr: "",
  hero_title_nl: "",
  hero_subtitle_fr: "",
  hero_subtitle_nl: "",
  legal_owner: "Prodinvideo SPRL",
  legal_owner_url: "https://prodinvideo.be",
  copyright_text: "Concept propriétaire · Prodinvideo SPRL",
  // Referral / Network advantage
  referral_enabled: true,
  referral_discount_pct: 6,
  referral_saving_adult: 42,
  referral_saving_junior: 36,
  referral_period_label_fr: "annuel",
  referral_period_label_nl: "jaarlijks",
  referral_url: "https://www.bhealth.be",
  referral_title_fr: "",
  referral_title_nl: "",
  referral_note_fr: "",
  referral_note_nl: "",
};

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [loaded, setLoaded] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/config");
        if (alive) {
          setBrand({ ...DEFAULT_BRAND, ...data });
        }
      } catch {
        // keep defaults
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Preview: apply a draft config locally without persisting.
  const startPreview = (draftCfg) => {
    setBrand({ ...DEFAULT_BRAND, ...draftCfg });
    setPreviewing(true);
    try { sessionStorage.setItem("brand_preview_draft", JSON.stringify(draftCfg)); } catch (_) {}
  };
  const stopPreview = async () => {
    setPreviewing(false);
    try { sessionStorage.removeItem("brand_preview_draft"); } catch (_) {}
    try {
      const { data } = await api.get("/config");
      setBrand({ ...DEFAULT_BRAND, ...data });
    } catch (_) {}
  };

  // Rehydrate preview across SPA navigations (same tab only)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("brand_preview_draft");
      if (raw) {
        const draft = JSON.parse(raw);
        setBrand((b) => ({ ...b, ...draft }));
        setPreviewing(true);
      }
    } catch (_) {}
  }, []);

  // Inject dynamic CSS that overrides navy/lime classes with brand colors.
  // We keep Tailwind's HSL statically compiled so modifiers (bg-navy-900/10) keep working,
  // while adding runtime overrides via a <style> tag for the solid classes.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", brand.color_primary);
    root.style.setProperty("--brand-primary-dark", brand.color_primary_dark);
    root.style.setProperty("--brand-accent", brand.color_accent);
    root.style.setProperty("--brand-accent-dark", brand.color_accent_dark);
    if (brand.center_short) {
      document.title = `${brand.center_short} — Local Benefits`;
    }
    // Favicon swap
    if (brand.favicon_url) {
      let link = document.querySelector('link[rel~="icon"]');
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = brand.favicon_url;
    }
  }, [brand]);

  const refresh = async () => {
    try {
      const { data } = await api.get("/config");
      setBrand({ ...DEFAULT_BRAND, ...data });
      setPreviewing(false);
      try { sessionStorage.removeItem("brand_preview_draft"); } catch (_) {}
      return data;
    } catch {
      return null;
    }
  };

  return (
    <BrandContext.Provider value={{ brand, loaded, refresh, setBrand, previewing, startPreview, stopPreview }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error("useBrand must be used within BrandProvider");
  return ctx;
}
